/**
 * Translation key + English fallback for every CloudStorage error path the
 * controller logs. Split into two source maps for clarity:
 *
 *   - TG_STORAGE_ERROR_MESSAGES — documented Telegram CloudStorage error
 *     codes, verified via the console-smoke runbook.
 *   - CLIENT_STORAGE_ERROR_MESSAGES — synthetic codes the controller throws
 *     itself (serialize/deserialize failures, success=false rejections,
 *     unknown-input catch-all). These never come back from TG.
 *
 * Object spread merges them into the public STORAGE_ERROR_MESSAGES so
 * consumers, getStorageErrorMessage, and the derived STORAGE_ERROR_CODE
 * see one combined table. Mirrors the HTTP_STATUS_MESSAGES +
 * NON_HTTP_MESSAGES split in shared/redux/middleware/rtk-error-middleware.ts.
 *
 * Both the outer object and each entry are runtime-frozen so a consumer
 * mutation (e.g. `entry.fallback = "evil"`) can't leak across call sites.
 */
interface StorageErrorMessage {
  readonly key: string
  readonly fallback: string
}

const TG_STORAGE_ERROR_MESSAGES = {
  DATA_TOO_LONG: {
    key: "telegram-cloud-storage-error-data-too-long",
    fallback: "Saved value exceeds the 4 KB size limit"
  },
  STORAGE_KEY_INVALID: {
    key: "telegram-cloud-storage-error-key-invalid",
    fallback: "Storage key contains characters that are not allowed"
  },
  STORAGE_KEY_TOO_LONG: {
    key: "telegram-cloud-storage-error-key-too-long",
    fallback: "Storage key is longer than 128 characters"
  },
  STORAGE_KEY_REQUIRED: {
    key: "telegram-cloud-storage-error-key-required",
    fallback: "Storage key cannot be empty"
  }
} as const satisfies Record<string, StorageErrorMessage>

const CLIENT_STORAGE_ERROR_MESSAGES = {
  SAVE_FAILED: {
    key: "telegram-cloud-storage-error-save-failed",
    fallback: "An unexpected error occurred while saving"
  },
  DELETE_FAILED: {
    key: "telegram-cloud-storage-error-delete-failed",
    fallback: "An unexpected error occurred while deleting"
  },
  LIST_KEYS_FAILED: {
    key: "telegram-cloud-storage-error-list-keys-failed",
    fallback: "An unexpected error occurred while listing keys"
  },
  SERIALIZATION_ERROR: {
    key: "telegram-cloud-storage-error-serialization",
    fallback: "Failed to serialize the value"
  },
  DESERIALIZATION_ERROR: {
    key: "telegram-cloud-storage-error-deserialization",
    fallback: "Failed to parse the stored value"
  },
  UNEXPECTED_GETITEMS_PAYLOAD: {
    key: "telegram-cloud-storage-error-unexpected-getitems-payload",
    fallback: "Telegram returned an unexpected payload shape"
  },
  UNKNOWN_ERROR: {
    key: "telegram-cloud-storage-error-unknown",
    fallback: "An unexpected storage error occurred"
  }
} as const satisfies Record<string, StorageErrorMessage>

const MESSAGES = { ...TG_STORAGE_ERROR_MESSAGES, ...CLIENT_STORAGE_ERROR_MESSAGES }

Object.values(MESSAGES).forEach(entry => {
  Object.freeze(entry)
})

export const STORAGE_ERROR_MESSAGES = Object.freeze(MESSAGES)

/**
 * Enum-style constant of known storage error codes (Telegram +
 * controller-synthesized). The `satisfies` constraint locks the key set to
 * STORAGE_ERROR_MESSAGES — missing or extra keys here fail typecheck, so the
 * two tables can't drift. Use at call sites (e.g.
 * `logStorageError(ctx, STORAGE_ERROR_CODE.DATA_TOO_LONG)`) for autocomplete
 * and refactor-safe access instead of a bare string literal.
 */
export const STORAGE_ERROR_CODE = {
  DATA_TOO_LONG: "DATA_TOO_LONG",
  STORAGE_KEY_INVALID: "STORAGE_KEY_INVALID",
  STORAGE_KEY_TOO_LONG: "STORAGE_KEY_TOO_LONG",
  STORAGE_KEY_REQUIRED: "STORAGE_KEY_REQUIRED",
  SAVE_FAILED: "SAVE_FAILED",
  DELETE_FAILED: "DELETE_FAILED",
  LIST_KEYS_FAILED: "LIST_KEYS_FAILED",
  SERIALIZATION_ERROR: "SERIALIZATION_ERROR",
  DESERIALIZATION_ERROR: "DESERIALIZATION_ERROR",
  UNEXPECTED_GETITEMS_PAYLOAD: "UNEXPECTED_GETITEMS_PAYLOAD",
  UNKNOWN_ERROR: "UNKNOWN_ERROR"
} as const satisfies { [K in keyof typeof STORAGE_ERROR_MESSAGES]: K }
